import { UserIdentity } from "~constants/application"
import {
  ASSIGNEE_TYPE,
  PROOF_OF_OWNERSHIP_STATUS,
} from "~constants/proofOfOwnership"
import { ROLES } from "~rbac/constants/rbac-roles"
import { ProofOfOwnership } from "~shapes/proofOfOwnership"
import { asApiError } from "~utils/apiError"

// Sentinel pill value for the operator's "My validations" queue. Not a real POO
// state — it switches the create-portfolio list from the status-filtered list
// (GET .../proofs-of-ownership) to the assigned queue (GET .../assigned).
export const POO_ASSIGNED_VIEW = "ASSIGNED"

// The active list view: a real POO status filter, or the assigned-queue sentinel.
export type PooView = PROOF_OF_OWNERSHIP_STATUS | typeof POO_ASSIGNED_VIEW

// Elevated operators can override another operator's claim and inspect other
// queues (their own USER queue, another USER's queue, or a SYSTEM validator's).
export const isElevatedOperator = (user?: UserIdentity | null): boolean => {
  const roles = user?.authorities?.roles ?? []
  return (
    roles.includes(ROLES.ROLE_ADMIN) ||
    roles.includes(ROLES.ROLE_CUSTOMER_SUPPORT)
  )
}

export const isAdminOperator = (user?: UserIdentity | null): boolean =>
  (user?.authorities?.roles ?? []).includes(ROLES.ROLE_ADMIN)

// USER assignee ids share the loggedUser.id hashid space, so a direct compare
// tells us whether the current operator owns the claim.
export const isPooAssignedToMe = (
  item?: ProofOfOwnership | null,
  user?: UserIdentity | null,
): boolean =>
  !!item?.assignee &&
  item.assignee.type === ASSIGNEE_TYPE.USER &&
  !!user?.id &&
  item.assignee.id === user.id

// Only the claim owner can release, except ROLE_ADMIN who can release any claim.
export const canReleasePoo = (
  item?: ProofOfOwnership | null,
  user?: UserIdentity | null,
): boolean =>
  !!item?.assignee && (isAdminOperator(user) || isPooAssignedToMe(item, user))

// Backend error codes for the assignment / verdict endpoints, mapped to
// operator-facing copy. Codes come from proof-of-ownership-service.
const ASSIGNMENT_ERROR_MESSAGES: Record<string, string> = {
  ALREADY_BEING_VALIDATED:
    "This proof of ownership is already being validated by someone else.",
  NOT_ASSIGNEE: "You can't release a validation assigned to someone else.",
  INVALID_STATE_CHANGE:
    "This validation is assigned to another operator, so you can't finalize it.",
  INVALID_ASSIGNEE_TYPE: "Invalid assignee type.",
  MISSING_ASSIGNEE_ID: "An assignee id is required.",
  RESOURCE_NOT_FOUND: "This proof of ownership no longer exists.",
}

// Status-only fallbacks for errors the interceptor strips to a CustomApiError
// (401/403/500), where the backend code string is no longer available.
const ASSIGNMENT_STATUS_MESSAGES: Record<number, string> = {
  401: "You don't have permission to perform this action.",
  403: "You don't have permission to perform this action.",
}

// Resolves the best operator-facing message for a rejected assignment/verdict
// call: a code-specific message when recognised, then a status fallback, then
// the caller's generic fallback.
export const pooAssignmentErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const apiError = asApiError(error)
  if (!apiError) return fallback
  if (apiError.code && ASSIGNMENT_ERROR_MESSAGES[apiError.code]) {
    return ASSIGNMENT_ERROR_MESSAGES[apiError.code]
  }
  return ASSIGNMENT_STATUS_MESSAGES[apiError.status] ?? fallback
}
